/**
 * Shared API client singleton for the web SPA and the native shells.
 *
 * All React code should import { api } from '@/lib/apiClient' instead of
 * instantiating its own client — keeps the bearer token store consistent.
 *
 * Base URL:
 *   - Web dev:   '/v2' — Vite proxies to http://localhost:8080.
 *   - Web prod:  '/v2' — SPA and Laravel share an origin.
 *   - Native:    the WebView origin is capacitor://localhost, so relative
 *     URLs CANNOT reach the API — every call must target the deployed host
 *     (VITE_API_ORIGIN, default https://fantasy.draftedge.com). Sanctum
 *     runs in bearer-token mode there; CORS already allows the capacitor
 *     origin (laravel/config/cors.php).
 *   - `?native=1` dev preview intentionally KEEPS '/v2' so the preview
 *     exercises the local backend through the proxy (isRealNative() is
 *     false in a desktop browser).
 *
 * Token storage on native uses Capacitor's Preferences runtime bridge
 * (window.Capacitor.Plugins.Preferences) — durable, unlike WKWebView
 * localStorage which the OS may evict. Falls back to localStorage when the
 * bridge is missing.
 */
import {
  createApiClient,
  createLocalStorageTokenStore,
  type ApiClient,
  type TokenStore,
} from '@draftedge/api-client';
import { isRealNative } from '@/lib/platform';

/** '' on web (relative /v2); the deployed API origin inside Capacitor. */
export const API_ORIGIN: string = isRealNative()
  ? ((import.meta.env.VITE_API_ORIGIN as string | undefined) ?? 'https://fantasy.draftedge.com')
  : '';

function createNativeTokenStore(key = 'draftedge_token'): TokenStore {
  const prefs = window.Capacitor?.Plugins?.Preferences;
  if (!prefs) {
    return createLocalStorageTokenStore(key);
  }
  return {
    async get() {
      try {
        return (await prefs.get({ key })).value ?? null;
      } catch {
        return null;
      }
    },
    async set(token) {
      try {
        await prefs.set({ key, value: token });
      } catch {
        /* storage failure — the session just won't persist */
      }
    },
    async clear() {
      try {
        await prefs.remove({ key });
      } catch {
        /* ignore */
      }
    },
  };
}

export const api: ApiClient = createApiClient({
  baseUrl: `${API_ORIGIN}/v2`,
  tokenStore: isRealNative()
    ? createNativeTokenStore()
    : createLocalStorageTokenStore(),
  onUnauthorized: () => {
    // The Zustand auth store imports this module and listens for token
    // clearing via `bootstrap()` retrying — but proactively dispatch an event
    // so any subscribed UI can react immediately.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('draftedge:unauthorized'));
    }
  },
});
